import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import { Save, LayoutGrid, Palette } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import WidgetConfigurator, { DEFAULT_CONFIG } from '../../components/admin/WidgetConfigurator';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
  'America/Mexico_City',
  'America/Bogota',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

function formatTzLabel(tz) {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    return tz.replace(/_/g, ' ').replace(/\//g, ' / ') + ' (' + offset + ')';
  } catch {
    return tz;
  }
}

export default function Settings() {
  const navigate = useNavigate();
  const { tenant, refreshUser } = useAuthStore();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [saving, setSaving] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(DEFAULT_CONFIG);
  const [sessionTimeout, setSessionTimeout] = useState(30);

  // Firm-level fields (Pass 1A — merged from standalone FirmSettings page)
  const [firmState, setFirmState] = useState('');
  const [firmCounty, setFirmCounty] = useState('');
  const [firmTimezone, setFirmTimezone] = useState('');
  const [firmMainPhone, setFirmMainPhone] = useState('');
  const [firmMainEmail, setFirmMainEmail] = useState('');
  const [firmDefaultContingency, setFirmDefaultContingency] = useState('');
  const [firmDefaultAttorneyId, setFirmDefaultAttorneyId] = useState('');
  const [letterhead, setLetterhead] = useState({});
  const [availableAttorneys, setAvailableAttorneys] = useState([]);

  useEffect(() => {
    if (tenant) {
      setName(tenant.name || '');
      setTimezone(tenant.settings?.timezone || 'America/New_York');
      setDateFormat(tenant.settings?.dateFormat || 'MM/DD/YYYY');
      setWidgetConfig(tenant.settings?.widget_config || DEFAULT_CONFIG);
      setSessionTimeout(tenant.settings?.session_timeout_minutes || 30);
    }
  }, [tenant]);

  useEffect(() => {
    loadFirmSettings();
  }, []);

  const loadFirmSettings = async () => {
    try {
      const res = await adminApi.getTenant();
      const t = res.data?.tenant || {};
      setFirmState(t.firm_state || '');
      setFirmCounty(t.firm_county || '');
      setFirmTimezone(t.firm_timezone || '');
      setFirmMainPhone(t.firm_main_phone || '');
      setFirmMainEmail(t.firm_main_email || '');
      setFirmDefaultContingency(t.firm_default_contingency_pct != null ? String(t.firm_default_contingency_pct) : '');
      setFirmDefaultAttorneyId(t.firm_default_attorney_id || '');
      setLetterhead(t.firm_letterhead && typeof t.firm_letterhead === 'object' ? t.firm_letterhead : {});
      setAvailableAttorneys(res.data?.available_attorneys || []);
    } catch (err) {
      console.error('Load firm settings failed', err);
    }
  };

  const updateLetterhead = (key, val) => {
    setLetterhead(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateTenant({
        name,
        settings: { timezone, dateFormat, widget_config: widgetConfig, session_timeout_minutes: sessionTimeout },
        firm_state: firmState || null,
        firm_county: firmCounty || null,
        firm_timezone: firmTimezone || null,
        firm_main_phone: firmMainPhone || null,
        firm_main_email: firmMainEmail || null,
        firm_default_contingency_pct: firmDefaultContingency === '' ? null : parseFloat(firmDefaultContingency),
        firm_default_attorney_id: firmDefaultAttorneyId || null,
        firm_letterhead: letterhead,
      });
      await refreshUser();
      await loadFirmSettings();
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const now = new Date();
  let preview = '';
  try {
    preview = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true,
    }).format(now);
  } catch {
    preview = 'Invalid timezone';
  }

  return (
    <div className="max-w-2xl mx-auto">
      <AdminPageHeader title="Firm Settings" description="Firm name, timezone, date format, and preferences" icon={Palette} iconColor="text-pink-600" iconBg="bg-pink-50" />

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Firm Name</label>
            <span className={`text-xs ${name.length > 30 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>{name.length}/30</span>
          </div>
          <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={30}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
          <select value={timezone} onChange={e => setTimezone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            {TIMEZONES.map(tz => (
              <option key={tz} value={tz}>{formatTzLabel(tz)}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">All dates and times will be displayed in this timezone.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
          <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 mb-1">Preview (current time)</p>
          <p className="text-sm text-gray-900 font-mono">{preview}</p>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Security</h2>
        <p className="text-sm text-gray-500 mb-4">HIPAA-compliant session management settings.</p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Logout After Inactivity</label>
          <select value={sessionTimeout} onChange={e => setSessionTimeout(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            <option value={15}>15 minutes (strict — clinical/hospital)</option>
            <option value={30}>30 minutes (recommended — law firms)</option>
            <option value={60}>60 minutes (lenient)</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Users will see a 60-second warning before being logged out. HIPAA requires automatic session timeout.</p>
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </div>

      {/* Identity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Identity</h2>
        <p className="text-sm text-gray-500 mb-4">How the firm appears on documents and communications.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
            <input type="text" value={letterhead.legal_name || ''} onChange={e => updateLetterhead('legal_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="T5 Law Group, P.C." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DBA</label>
            <input type="text" value={letterhead.dba || ''} onChange={e => updateLetterhead('dba', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input type="text" value={letterhead.tagline || ''} onChange={e => updateLetterhead('tagline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Personal Injury Attorneys" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Identity'}
          </button>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Address</h2>
        <p className="text-sm text-gray-500 mb-4">Primary office address used on documents and letterhead.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
            <input type="text" value={letterhead.address_line_1 || ''} onChange={e => updateLetterhead('address_line_1', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input type="text" value={letterhead.address_line_2 || ''} onChange={e => updateLetterhead('address_line_2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input type="text" value={letterhead.city || ''} onChange={e => updateLetterhead('city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select value={firmState} onChange={e => setFirmState(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              <input type="text" value={letterhead.zip || ''} onChange={e => updateLetterhead('zip', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
            <input type="text" value={firmCounty} onChange={e => setFirmCounty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Orange" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Address'}
          </button>
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Contact Details</h2>
        <p className="text-sm text-gray-500 mb-4">Main phone, fax, email, and website.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Phone</label>
            <input type="text" value={firmMainPhone} onChange={e => setFirmMainPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="(949) 555-0100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
            <input type="text" value={letterhead.fax || ''} onChange={e => updateLetterhead('fax', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Email</label>
            <input type="email" value={firmMainEmail} onChange={e => setFirmMainEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="info@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input type="text" value={letterhead.website || ''} onChange={e => updateLetterhead('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="https://example.com" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Contact'}
          </button>
        </div>
      </div>

      {/* Practice */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Practice</h2>
        <p className="text-sm text-gray-500 mb-4">Firm timezone, default attorney, and contingency rate. Used by document generation and deadline calculation.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firm Timezone</label>
            <select value={firmTimezone} onChange={e => setFirmTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
              <option value="">—</option>
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{formatTzLabel(tz)}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Used for due dates, deadlines, and any timezone-aware action. Distinct from the Display Timezone above.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Attorney</label>
            <select value={firmDefaultAttorneyId} onChange={e => setFirmDefaultAttorneyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
              <option value="">—</option>
              {availableAttorneys.map(u => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Contingency %</label>
            <input type="number" step="0.01" min="0" max="100"
              value={firmDefaultContingency} onChange={e => setFirmDefaultContingency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="33.33" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Practice'}
          </button>
        </div>
      </div>

      {/* Bar / Tax */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Bar &amp; Tax</h2>
        <p className="text-sm text-gray-500 mb-4">State bar registration and tax identification numbers.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bar Number</label>
            <input type="text" value={letterhead.bar_number || ''} onChange={e => updateLetterhead('bar_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID (EIN)</label>
            <input type="text" value={letterhead.tax_id || ''} onChange={e => updateLetterhead('tax_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="88-1234567" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Bar / Tax'}
          </button>
        </div>
      </div>

      {/* IOLTA */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">IOLTA Trust Account</h2>
        <p className="text-sm text-gray-500 mb-4">Client trust account name and bank.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IOLTA Account Name</label>
            <input type="text" value={letterhead.iolta_account_name || ''} onChange={e => updateLetterhead('iolta_account_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IOLTA Bank Name</label>
            <input type="text" value={letterhead.iolta_bank_name || ''} onChange={e => updateLetterhead('iolta_bank_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save IOLTA'}
          </button>
        </div>
      </div>

      {/* Branding */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Branding</h2>
        <p className="text-sm text-gray-500 mb-4">Logo, default font, and email signature HTML.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input type="text" value={letterhead.logo_url || ''} onChange={e => updateLetterhead('logo_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="https://cdn.example.com/logo.png" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Font</label>
            <input type="text" value={letterhead.default_font || ''} onChange={e => updateLetterhead('default_font', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Times New Roman" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Font Size (pt)</label>
            <input type="number" min="8" max="24"
              value={letterhead.default_font_size || ''} onChange={e => updateLetterhead('default_font_size', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Signature (HTML)</label>
            <textarea rows={6} value={letterhead.email_signature_html || ''} onChange={e => updateLetterhead('email_signature_html', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={() => handleSave()} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Branding'}
          </button>
        </div>
      </div>

      {/* Widget Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
        <div className="flex items-center gap-2 mb-1">
          <LayoutGrid className="h-5 w-5 text-brand-600" />
          <h2 className="text-lg font-semibold text-gray-900">Record Page Widgets</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Drag widgets between columns to customize the record detail page layout. Changes apply to all users.</p>
        <WidgetConfigurator config={widgetConfig} onChange={setWidgetConfig} />
        <div className="flex justify-end mt-4">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Widget Layout'}
          </button>
        </div>
      </div>
    </div>
  );
}
